import * as MeetingItemActions from './meeting-item.actions';

import {createEntityAdapter, EntityAdapter, EntityState} from '@ngrx/entity';
import {createReducer, on} from '@ngrx/store';
import {DahLoadingType} from '@common/components-module/core-components.models';
import {DahPaginationModel, KeyWordModel} from '@common/models/commonHTTP.models';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {
    BoardMeetingParticipantModel,
    BoardMeetingTotals,
    Meeting,
    MeetingAgendaItem,
    MeetingPreparedItem,
    MeetingPreparedResponseTotal,
    MeetingStartedItem,
    ParticipantsSearchParams,
    VotingResultResponse
} from '@communication/_common/models/meeting.models';

export const meetingItemFeatureKey = 'item';

export const participantsDraftAdapter: EntityAdapter<MeetingPreparedItem> = createEntityAdapter<MeetingPreparedItem>();

export function sortBoardParticipantsByName(a: BoardMeetingParticipantModel, b: BoardMeetingParticipantModel) {
    return StoredComponentHelperService.sortArrayComparer(a, b, 'meetingUserName.fullName');
}

export const boardParticipantsDraftAdapter: EntityAdapter<BoardMeetingParticipantModel> = createEntityAdapter<BoardMeetingParticipantModel>({
    selectId: (participant) => participant.userId!,
    sortComparer: sortBoardParticipantsByName
});

export const participantsStartedAdapter: EntityAdapter<MeetingStartedItem> = createEntityAdapter<MeetingStartedItem>();

export interface MeetingItemState {
    item: ItemState;
    participantsDraft: ParticipantsDraftState;
    boardParticipantsDraft: BoardParticipantsDraftState;
    participantsStarted: ParticipantsStartedState;
}

export interface ItemState {
    meeting?: Meeting;
    agenda?: MeetingAgendaItem[];
    results?: VotingResultResponse;
}

export interface ParticipantsDraftState extends EntityState<MeetingPreparedItem> {
    totals?: MeetingPreparedResponseTotal;
    loading?: DahLoadingType;
    itemsLoading?: string[];
    pagination?: DahPaginationModel;
    filter?: ParticipantsSearchParams;
}

export interface BoardParticipantsDraftState extends EntityState<BoardMeetingParticipantModel> {
    totals?: BoardMeetingTotals;
    loading?: DahLoadingType;
    itemsLoading?: string[];
    pagination?: DahPaginationModel;
    filter?: KeyWordModel;
}

export interface ParticipantsStartedState extends EntityState<MeetingStartedItem> {
    notifiable?: boolean,
    loading?: DahLoadingType;
    itemsLoading?: string[];
    pagination?: DahPaginationModel;
    filter?: ParticipantsSearchParams;
}

export const itemInitialState: ItemState = {};

export const participantsDraftInitialState: ParticipantsDraftState = participantsDraftAdapter.getInitialState({
    loading: 'listLoading',
    itemsLoading: []
});
export const boardParticipantsDraftInitialState: BoardParticipantsDraftState = boardParticipantsDraftAdapter.getInitialState({
    loading: 'listLoading',
    itemsLoading: []
});

export const participantsActiveInitialState: ParticipantsStartedState = participantsStartedAdapter.getInitialState({
    loading: 'listLoading',
    itemsLoading: []
});

export const initialState: MeetingItemState = {
    item: itemInitialState,
    participantsDraft: participantsDraftInitialState,
    boardParticipantsDraft: boardParticipantsDraftInitialState,
    participantsStarted: participantsActiveInitialState
};

export const meetingItemReducer = createReducer(
    initialState,

    on(MeetingItemActions.ClearEntities, state => ({
        ...state,
        participantsDraft: participantsDraftAdapter.removeAll({
            ...state.participantsDraft
        }),
        participantsStarted: participantsStartedAdapter.removeAll({
            ...state.participantsStarted
        })
    })),

    // <editor-fold desc="Meeting item - main">
    on(MeetingItemActions.GetMeetingItemSuccess, (state, action) => ({
        ...state,
        item: {
            ...state.item,
            meeting: {
                // ...state.item?.meeting,
                ...action.payload,
                proposalDateTime: action.payload.proposalDateTime ? action.payload.proposalDateTime : "",
                proposalDescription: action.payload.proposalDescription ? action.payload.proposalDescription : "",
                files: action.payload.files ? action.payload?.files : [],
                initiators: action.payload.initiators ? action.payload?.initiators : []
            }
        }
    })),

    on(MeetingItemActions.UpdateMeetingAgendaCount, (state, action) => ({
        ...state,
        item: {
            ...state.item,
            meeting: {
                ...state.item?.meeting,
                agendaCount: action.payload
            }
        }
    })),

    on(MeetingItemActions.LoadAgendaSuccess, (state, action) => ({
        ...state,
        item: {
            ...state.item,
            agenda: action.payload.items
        }
    })),

    on(MeetingItemActions.LoadResultsSuccess, (state, action) => ({
        ...state,
        item: {
            ...state.item,
            results: action.payload,
        }
    })),

    on(MeetingItemActions.SetResultsForceUpdate, (state) => ({
        ...state,
        item: {
            ...state.item,
            results: {
                items: state?.item?.results?.items ? state?.item?.results?.items : [],
                forceUpdate: true
            },
        }
    })),

    // </editor-fold>

    // <editor-fold desc="Meeting item - Participants Draft">
    on(MeetingItemActions.LoadParticipantsDraft, (state, action) => ({
        ...state,
        participantsDraft: {
            ...state.participantsDraft,
            loading: action.payload.loading
        }
    })),

    on(MeetingItemActions.LoadParticipantsDraftSuccess, (state, action) => {
        return ({
            ...state,
            participantsDraft: action.payload?.clearState
                ? participantsDraftAdapter.setAll(action.payload.items, {
                    ...state.participantsDraft,
                    totals: action.payload.totals,
                    loading: undefined,
                    pagination: {...action.payload.pagination}
                })
                : participantsDraftAdapter.setMany(action.payload.items, {
                    ...state.participantsDraft,
                    totals: action.payload.totals,
                    loading: undefined,
                    pagination: {...action.payload.pagination}
                })
        });
    }),

    on(MeetingItemActions.LoadParticipantsDraftFailed, (state) => ({
        ...state,
        participantsDraft: {
            ...state.participantsDraft,
            loading: undefined
        }
    })),

    on(MeetingItemActions.SetParticipantsDraftPagination, (state, action) => ({
        ...state,
        participantsDraft: {
            ...state.participantsDraft,
            pagination: {
                ...state.participantsDraft?.pagination,
                number: action.payload ? StoredComponentHelperService.setNextPage(state.participantsDraft?.pagination, state.participantsDraft?.ids?.length) : 0
            }
        }
    })),

    on(MeetingItemActions.SetParticipantsDraftFilter, (state, action) => ({
        ...state,
        participantsDraft: {
            ...state.participantsDraft,
            filter: action.payload
        }
    })),

    on(MeetingItemActions.AddParticipantDraftItems, MeetingItemActions.DeleteParticipantDraftItems, (state) => ({
        ...state,
        participantsDraft: {
            ...state.participantsDraft,
            loading: 'listLoading' as DahLoadingType
        }
    })),

    on(MeetingItemActions.UpdateParticipantDraftItemSuccess, (state, action) => {
        if (!action.payload.item?.id) {
            return {...state};
        }

        return ({
            ...state,
            participantsDraft: participantsDraftAdapter.updateOne({id: action.payload.item.id, changes: action.payload.item}, {
                ...state.participantsDraft,
                totals: action.payload?.totals ? action.payload?.totals : state.participantsDraft?.totals
            })
        });
    }),

    on(MeetingItemActions.DeleteParticipantDraftItemsSuccess, (state, action) => {
        return ({
            ...state,
            participantsDraft: participantsDraftAdapter.removeMany(action.payload.items, {
                ...state.participantsDraft,
                totals: action.payload.totals,
                loading: undefined,
                pagination: {...state.participantsDraft?.pagination, totalElements: action.payload.totals.totalMeetingApartments}
            })
        });
    }),

    on(MeetingItemActions.SetParticipantsDraftItemsLoading, (state, action) => {
        return ({
            ...state,
            participantsDraft: {
                ...state.participantsDraft,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.participantsDraft.itemsLoading, action.payload.id, action.payload.isStart)
            }
        });
    }),
    // </editor-fold>

    // <editor-fold desc="Meeting Board item - Participants Draft">
    on(MeetingItemActions.LoadBoardParticipantsDraft, (state, action) => ({
        ...state,
        boardParticipantsDraft: {
            ...state.boardParticipantsDraft,
            loading: action.payload.loading
        }
    })),

    on(MeetingItemActions.LoadBoardParticipantsDraftSuccess, (state, action) => {
        return ({
            ...state,
            boardParticipantsDraft: action.payload?.clearState
                ? boardParticipantsDraftAdapter.setAll(action.payload.items, {
                    ...state.boardParticipantsDraft,
                    totals: action.payload.totals,
                    loading: undefined,
                    pagination: {...action.payload.pagination}
                })
                : boardParticipantsDraftAdapter.setMany(action.payload.items, {
                    ...state.boardParticipantsDraft,
                    totals: action.payload.totals,
                    loading: undefined,
                    pagination: {...action.payload.pagination}
                })
        });
    }),

    on(MeetingItemActions.LoadBoardParticipantsDraftFailed, (state) => ({
        ...state,
        boardParticipantsDraft: {
            ...state.boardParticipantsDraft,
            loading: undefined
        }
    })),

    on(MeetingItemActions.SetBoardParticipantsDraftPagination, (state, action) => ({
        ...state,
        boardParticipantsDraft: {
            ...state.boardParticipantsDraft,
            pagination: {
                ...state.boardParticipantsDraft?.pagination,
                number: action.payload ? StoredComponentHelperService.setNextPage(state.boardParticipantsDraft?.pagination, state.boardParticipantsDraft?.ids?.length) : 0
            }
        }
    })),

    on(MeetingItemActions.SetBoardParticipantsDraftFilter, (state, action) => ({
        ...state,
        boardParticipantsDraft: {
            ...state.boardParticipantsDraft,
            filter: action.payload
        }
    })),

    on(MeetingItemActions.AddBoardParticipantDraftItems, MeetingItemActions.DeleteBoardParticipantDraftItems, (state) => ({
        ...state,
        boardParticipantsDraft: {
            ...state.boardParticipantsDraft,
            loading: 'listLoading' as DahLoadingType
        }
    })),

    on(MeetingItemActions.UpdateBoardParticipantDraftItemSuccess, (state, action) => {
        if (!action.payload.item?.userId) {
            return {...state};
        }

        return ({
            ...state,
            boardParticipantsDraft: boardParticipantsDraftAdapter.updateOne({id: action.payload.item.userId, changes: action.payload.item}, {
                ...state.boardParticipantsDraft,
            })
        });
    }),

    on(MeetingItemActions.DeleteBoardParticipantDraftItemsSuccess, (state, action) => {
        return ({
            ...state,
            boardParticipantsDraft: boardParticipantsDraftAdapter.removeMany(action.payload.items, {
                ...state.boardParticipantsDraft,
                loading: undefined,
                pagination: {...state.boardParticipantsDraft?.pagination, totalElements: action.payload.totals?.participants}
            })
        });
    }),

    on(MeetingItemActions.SetParticipantsDraftItemsLoading, (state, action) => {
        return ({
            ...state,
            participantsDraft: {
                ...state.participantsDraft,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.participantsDraft.itemsLoading, action.payload.id, action.payload.isStart)
            }
        });
    }),
    // </editor-fold>

    // <editor-fold desc="Meeting item - Participants Started">
    on(MeetingItemActions.LoadParticipantsStarted, (state, action) => ({
        ...state,
        participantsStarted: {
            ...state.participantsStarted,
            loading: action.payload.loading
        }
    })),

    on(MeetingItemActions.LoadParticipantsStartedSuccess, (state, action) => {
        return ({
            ...state,
            participantsStarted: action.payload?.clearState
                ? participantsStartedAdapter.setAll(action.payload.items, {
                    ...state.participantsStarted,
                    notifiable: action.payload.notifiable,
                    loading: undefined,
                    pagination: {...action.payload.pagination}
                })
                : participantsStartedAdapter.setMany(action.payload.items, {
                    ...state.participantsStarted,
                    notifiable: action.payload.notifiable,
                    loading: undefined,
                    pagination: {...action.payload.pagination}
                })
        });
    }),

    on(MeetingItemActions.LoadParticipantsStartedFailed, (state) => ({
        ...state,
        participantsStarted: {
            ...state.participantsStarted,
            loading: undefined
        }
    })),

    on(MeetingItemActions.SetParticipantsStartedPagination, (state, action) => ({
        ...state,
        participantsStarted: {
            ...state.participantsStarted,
            pagination: {
                ...state.participantsStarted?.pagination,
                number: action.payload ? StoredComponentHelperService.setNextPage(state.participantsStarted?.pagination, state.participantsStarted.ids?.length) : 0
            }
        }
    })),

    on(MeetingItemActions.SetParticipantsStartedFilter, (state, action) => ({
        ...state,
        participantsStarted: {
            ...state.participantsStarted,
            filter: action.payload
        }
    })),

    on(
        MeetingItemActions.AddParticipantStartedItems,
        MeetingItemActions.DeleteParticipantStartedItems,
        (state) => ({
            ...state,
            participantsStarted: {
                ...state.participantsStarted,
                loading: 'listLoading' as DahLoadingType
            }
        })),

    on(MeetingItemActions.SetParticipantsStartedLoading, (state, action) => ({
        ...state,
        participantsStarted: {
            ...state.participantsStarted,
            loading: action.payload
        }
    })),

    on(MeetingItemActions.DeleteParticipantStartedItemsSuccess, (state, action) => {
        return ({
            ...state,
            participantsStarted: participantsStartedAdapter.removeMany(action.payload.items, {
                ...state.participantsStarted,
                totals: action.payload.totals,
                loading: undefined,
            })
        });
    }),

    on(MeetingItemActions.SetParticipantsStartedItemsLoading, (state, action) => {
        return ({
            ...state,
            participantsStarted: {
                ...state.participantsStarted,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.participantsStarted.itemsLoading, action.payload.id, action.payload.isStart)
            }
        });
    }),

    on(MeetingItemActions.ClearParticipantsStartedItemsLoading, (state) => {
        return {
            ...state,
            participantsStarted: {
                ...state.participantsStarted,
                itemsLoading: []
            }
        };
    }),


    on(MeetingItemActions.UpdateParticipantStartedItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.participantsStarted.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return ({
                ...state,
                participantsStarted: participantsStartedAdapter.updateOne({id: action.payload.id!, changes: action.payload}, {
                    ...state.participantsStarted,
                    loading: undefined,
                })
            });
        }

        return ({
            ...state,
            participantsStarted: participantsStartedAdapter.removeOne(action.payload.id!, {
                ...state.participantsStarted,
                pagination: {
                    ...state.participantsStarted.pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.participantsStarted.pagination, state?.participantsStarted?.ids, true)
                },
                loading: undefined,
            })
        });

    }),
    // </editor-fold>

    on(MeetingItemActions.SetInitialState, () => ({...initialState}))
);

export const {selectAll: selectAllDraft} = participantsDraftAdapter.getSelectors();
export const {selectAll: selectAllBoardDraft} = boardParticipantsDraftAdapter.getSelectors();
export const {selectAll: selectAllStarted} = participantsStartedAdapter.getSelectors();

const isAvailableByFilter = (searchParams: ParticipantsSearchParams | undefined, item: MeetingStartedItem): boolean => {
    if (!searchParams) {
        return true;
    }

    if (searchParams.query) {
        const query = searchParams.query.toLowerCase();
        const {ownerName} = item.user;

        if (
            !(ownerName?.firstName?.toLowerCase().includes(query) ||
                ownerName?.lastName?.toLowerCase().includes(query) ||
                ownerName?.fullName?.toLowerCase().includes(query) ||
                ownerName?.parentalName?.toLowerCase().includes(query) ||
                ownerName?.shortName?.toLowerCase().includes(query))
        ) {
            return false;
        }
    }

    if (searchParams.building && item.building?.id !== searchParams.building.id) {
        return false;
    }

    if (searchParams.section && item.section?.id !== searchParams.section.id) {
        return false;
    }

    if (searchParams.status && item.user.userStatus !== searchParams.status) {
        return false;
    }

    if ((searchParams.iNotified || searchParams.iNotified === false) && item.invitation.notified !== searchParams.iNotified) {
        return false;
    }

    if ((searchParams.iFilePresent || searchParams.iFilePresent === false) && !!item.invitation.organizationFile !== searchParams.iFilePresent) {
        return false;
    }

    if ((searchParams.iSignChecked || searchParams.iSignChecked === false) && item.invitation.checked !== searchParams.iSignChecked) {
        return false;
    }

    if ((searchParams.iSignDigital || searchParams.iSignDigital === false) && item.invitation.signed && item.invitation.originUnsigned === searchParams.iSignDigital) {
        return false;
    }

    if ((searchParams.iSecondSign || searchParams.iSecondSign === false) && item.invitation.originUnsigned !== searchParams.iSecondSign) {
        return false;
    }

    if ((searchParams.vOffline || searchParams.vOffline === false) && item.votingList?.notified !== searchParams.vOffline) {
        return false;
    }

    if ((searchParams.vSigned || searchParams.vSigned === false) && !!item.votingList.organizationFile !== searchParams.vSigned) {
        return false;
    }

    if ((searchParams.vSignChecked || searchParams.vSignChecked === false) && item.votingList.checked !== searchParams.vSignChecked) {
        return false;
    }

    if ((searchParams.vSignDigital || searchParams.vSignDigital === false) && item.votingList.signed && item.votingList.originUnsigned === searchParams.vSignDigital) {
        return false;
    }

    return !((searchParams.vSecondSign || searchParams.vSecondSign === false) && item.votingList.originUnsigned !== searchParams.vSecondSign);


};
